import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  TextField,
  Chip,
  Fade,
  Collapse,
  Checkbox,
  FormGroup,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  root: {
    overflow: "hidden",
    background: "#ffffff",
    color: "#1a1d2e",
  },
  progressStrip: {
    height: 3,
    background: "#f0f1f5",
  },
  progressBar: {
    height: 3,
    background: "linear-gradient(90deg, #5b52f0, #38bfa1)",
    borderRadius: "0 3px 3px 0",
    transition: "width 0.5s cubic-bezier(.4,0,.2,1)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px 10px",
    borderBottom: "1px solid #f0f1f5",
  },
  scopeChip: {
    background: "#eff6ff",
    color: "#3B82F6 !important",
    border: "1px solid #bfdbfe",
    fontSize: 11,
    height: 22,
    fontFamily: "inherit",
    fontWeight: 500,
    "& .MuiChip-label": { 
      padding: "0 8px",
      maxWidth: '500px',
      textOverflow: 'ellipsis'
    },
  },
  body: {
    padding: "14px 16px 8px",
  },
  questionText: {
    fontSize: 13.5,
    fontWeight: 600,
    lineHeight: 1.55,
    color: "#1a1d2e",
    marginBottom: 12,
    letterSpacing: 0.05,
  },
  optionCard: {
    display: "flex",
    alignItems: "center",
    margin: "0 0 6px",
    borderRadius: 8,
    border: "1.5px solid #e8eaf0",
    padding: "7px 12px 7px 8px",
    cursor: "pointer",
    background: "#fafbfc",
    transition: "border-color 0.15s, background 0.15s, transform 0.1s",
    "&:hover": {
        background: "#eff6ff",
        borderColor: "#93c5fd",
        transform: "translateX(2px)",
    },
  },
  optionCardSelected: {
    background: "#dbeafe",
    borderColor: "#3B82F6",
    "&:hover": {
      background: "#dbeafe",
      borderColor: "#3B82F6",
    },
  },
  formLabel: {
    margin: 0,
    flex: 1,
    "& .MuiFormControlLabel-label": {
      fontSize: 13,
      color: "#3a3d52",
      fontFamily: "inherit",
      lineHeight: 1.4,
      overflowWrap: 'anywhere'
    },
  },
  radio: {
    color: "#c5c8d8",
    padding: "0 8px 0 4px",
    "&.Mui-checked": {
      color: "#3B82F6",
    },
  },
  otherField: {
    marginTop: 4,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      background: "#fafbfc",
      color: "#1a1d2e",
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 400,
      padding: 10,
      "& fieldset": { borderColor: "#e0e3eb" },
      "&:hover fieldset": { borderColor: "#b8b3f8" },
      "&.Mui-focused fieldset": { borderColor: "#3B82F6", borderWidth: 1.5  },
    },
    "& .MuiInputBase-input::placeholder": {
      color: "#aab0c0",
      opacity: 1,
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px 12px",
    borderTop: "1px solid #f0f1f5",
    marginTop: 6,
  },
  stepLabel: {
    fontSize: 11,
    color: "#a0a5b8",
    fontFamily: "inherit",
    letterSpacing: 0.2,
  },
  btnGroup: {
    display: "flex",
    gap: 8,
  },
  skipBtn: {
    fontSize: 12,
    color: "#8b91a8",
    fontFamily: "inherit",
    textTransform: "none",
    padding: "5px 12px",
    borderRadius: 7,
    minWidth: 0,
    fontWeight: 500,
    "&:hover": {
        background: "#eff6ff",
        color: "#3B82F6",
    },
  },
  submitBtn: {
    fontSize: 12,
    fontFamily: "inherit",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 7,
    padding: "5px 16px",
    background: "#3B82F6",
    color: "#fff",
    letterSpacing: 0.2,
    boxShadow: "0 2px 8px rgba(59,130,246,0.22)",
    transition: "background 0.15s, transform 0.1s, box-shadow 0.15s",
    "&:hover": {
        background: "#2563eb",
        transform: "translateY(-1px)",
        boxShadow: "0 4px 14px rgba(59,130,246,0.32)",
    },
    "&:disabled": {
      background: "#e0e3eb",
      color: "#a0a5b8",
      boxShadow: "none",
      transform: "none",
    },
  },
  confirmedRoot: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "12px 16px",
    background: "#eff6ff",
    border: "1.5px solid #bfdbfe",
    borderRadius: 10,
    boxShadow: "0 2px 8px rgba(91,82,240,0.08)",
  },
  checkCircle: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#3B82F6",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    flexShrink: 0,
  },
  confirmedLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#5b52f0",
    fontFamily: "inherit",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  confirmedAnswer: {
    fontSize: 12,
    color: "#3B82F6",  
    fontFamily: "inherit",
    marginTop: 1,
    lineHeight: 1.4,
  },
}));

const formatList = (items) => {
  if (items.length <= 1) return items[0] || "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

/**
 * ClarificationQuestion
 *
 * Props:
 *  question    {string}    – The question text
 *  options     {string[]}  – Selectable radio options
 *  allowOther  {boolean}   – Show a free-text "Other" option (default: true)
 *  currentStep {number}    – 1-based step index
 *  totalSteps  {number}    – Total number of questions
 *  scopeLabel  {string}    – Header chip label
 *  onSubmit    {(answer: string) => void}
 *  onSkip      {() => void}
 */
const ClarificationQuestion = ({
  question,
  options = [],
  allowOther = true,
  currentStep = 1,
  totalSteps = 3,
  scopeLabel = "Needed to define scope",
  onSubmit,
  onSkip,
  isMultipleChoiceAllowed
}) => {
  const classes = useStyles();
  const OTHER_VALUE = "__other__";
  const otherField = useRef()

  const [selected, setSelected] = useState("");
  const [selectedList, setSelectedList] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const isChecked = (value) =>
    isMultipleChoiceAllowed ? selectedList.includes(value) : selected === value;
  const isOtherSelected = isChecked(OTHER_VALUE);
  const hasSelection = isMultipleChoiceAllowed ? selectedList.length > 0 : !!selected;

  const getAnswer = () => {
    const picked = isMultipleChoiceAllowed
      ? [...options, OTHER_VALUE].filter((v) => selectedList.includes(v)) // keep option order
      : [selected];
    return formatList(picked.map((v) => (v === OTHER_VALUE ? otherText.trim() : v)));
  };
    

  const handleSelect = (value) => {
    if (isMultipleChoiceAllowed) {
      setSelectedList((prev) =>
        prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
      );
    } else {
      setSelected(value);
    }
    setError("");
  };

  const renderControl = (value) =>
    isMultipleChoiceAllowed ? (
      <Checkbox
        size="small"
        className={classes.radio}
        checked={isChecked(value)}
        onChange={() => handleSelect(value)}
      />
    ) : (
      <Radio size="small" className={classes.radio} />
    );

  const OptionGroup = isMultipleChoiceAllowed ? FormGroup : RadioGroup;
  const groupProps = isMultipleChoiceAllowed
    ? {}
    : { value: selected, onChange: (e) => handleSelect(e.target.value) };

  useEffect(() => {
    if (isOtherSelected) {
      setTimeout(() => {
        otherField.current?.focus();
      }, 0);
    }
  },[isOtherSelected])

  const handleSubmit = () => {
    if (!hasSelection) {
      setError(isMultipleChoiceAllowed ? "Please select at least one option to continue." : "Please select an option to continue.");
      return;
    }
    if (isOtherSelected && !otherText.trim()) { setError("Please describe your use case."); return; }
    setError("");
    setSubmitted(true);
    onSubmit?.(getAnswer());
  };

  const handleSkip = () => { setError(""); onSkip?.(); };

  if (submitted) {
    return (
      <Fade in>
         <Box className={classes.body}>
          <Typography>{question}</Typography>
         </Box>
      </Fade>
    );
  }

  return (
    <Fade in>
      <Box className={classes.root}>
        {/* Header chip */}
        <Box className={classes.header}>
          <Chip label={`⊙ ${scopeLabel}`} size="small" className={classes.scopeChip} />
        </Box>

        {/* Question + options */}
        <Box className={classes.body}>
          <Typography className={classes.questionText}>{question}</Typography>

          <OptionGroup {...groupProps}>
            {options.map((opt) => (
              <Box
                key={opt}
                className={`${classes.optionCard} ${isChecked(opt) ? classes.optionCardSelected : ""}`}
                onClick={() => handleSelect(opt)}
              >
                <FormControlLabel
                  value={opt}
                  control={renderControl(opt)}
                  label={opt}
                  className={classes.formLabel}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            ))}

            {allowOther && (
              <>
                <Box
                  className={`${classes.optionCard} ${isOtherSelected ? classes.optionCardSelected : ""}`}
                  onClick={() => handleSelect(OTHER_VALUE)}
                >
                  <FormControlLabel
                    value={OTHER_VALUE}
                    control={renderControl(OTHER_VALUE)}
                    label="Other — describe your use case…"
                    className={classes.formLabel}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>

                <Collapse in={isOtherSelected}>
                  <TextField
                    inputRef={otherField}
                    className={classes.otherField}
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    variant="outlined"
                    placeholder="Describe your use case…"
                    value={otherText}
                    onChange={(e) => { setOtherText(e.target.value); if (error) setError(""); }}
                    inputProps={{ maxLength: 400 }}
                  />
                </Collapse>
              </>
            )}
          </OptionGroup>

          <Collapse in={!!error}>
            <Typography style={{ fontSize: 11, color: "#e05252", marginTop: 6, fontFamily: "inherit", paddingLeft: 2 }}>
              {error}
            </Typography>
          </Collapse>
        </Box>

        {/* Footer */}
        <Box className={classes.footer}>
          <Typography className={classes.stepLabel}>
          </Typography>
          <Box className={classes.btnGroup}>
            <Button className={classes.skipBtn} onClick={handleSkip}>Skip</Button>
            <Button
              className={classes.submitBtn}
              onClick={handleSubmit}
              disabled={!hasSelection || (isOtherSelected && !otherText.trim())}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default ClarificationQuestion;